import Config from "@readai/common/config";
import type { RedirectTo } from "@readai/common/types";

/**
 * Takes in a path and composes the corresponding url w/ protocol. If no app is passed,
 * will default to the current `window.origin`.
 * @example
 *  getAppUrl("signup") or getAppUrl("/signup") => "https://app.read.ai/signup"
 */
export const getAppUrl = (path: string, opts?: { app: "booking" | "main" }) => {
  if (path.includes("http")) return path;

  const urlPath = path.startsWith("/") ? path : `/${path}`;

  const rootPath = opts
    ? opts.app === "booking"
      ? Config.RSS_LINKS_URL
      : Config.FRONTEND_URL
    : window.origin;

  return `${rootPath}${urlPath}`;
};

export const getApiUrl = (path: string) => {
  if (path.includes("http")) return path;
  const urlPath = path.startsWith("/") ? path : `/${path}`;
  const rootPath = Config.api.HOST;
  return `${rootPath}${urlPath}`;
};

export const getRelativeUrl = (path: string) => {
  if (path.includes("http")) {
    return path.replace(window.origin, "");
  }
  return path;
};

/** Returns corresponding url for a path, minus the protocol. Used for text/display purposes */
export const getDisplayAppUrl = (path: string) => {
  return getAppUrl(path).replace(/(^\w+:|^)\/\//, "");
};

export const isLinkExternal = (link?: RedirectTo | null) => {
  if (!link?.pathname || !isValidUrl(link.pathname)) {
    return false;
  }

  if (
    link.search &&
    new URLSearchParams(link.search).get("externalRedirect") === "true"
  ) {
    return true;
  }

  const url = new URL(decodeURIComponent(link.pathname));
  return url.origin !== window.location.origin;
};

export const isValidUrl = (link: string | null) => {
  if (!link) {
    return false;
  }

  const urlPattern = new RegExp(
    "(?:https?)://(w+:?w*)?(S+)(:d+)?(/|/([w#!:.?+=&%!-/]))?",
  );

  return urlPattern.test(link);
};
